import React, { useState, useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import 'leaflet-polylinedecorator';
import '@utils/leaflet-polygon-fill';
import L, { icon, latLngBounds } from 'leaflet';

import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  CircleMarker,
  Polyline,
  Polygon,
  SVGOverlay,
  LayerGroup,
} from 'react-leaflet';

import { MapProps } from 'types/map';

import 'leaflet/dist/leaflet.css';

const pinIcon = require('assets/pin.svg');
const pinIconLight = require('assets/pin_light.svg');

const parkCenter: [number, number] = [40.83981876065034, -72.61720418930055];
const bounds = latLngBounds([
  [40.850593, -72.64106],
  [40.826874, -72.584836],
]);
const defaultPathColor = '#152B61';
const testPath = [];

function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
        result[3],
        16,
      )}`
    : null;
}

const arrow = (color = defaultPathColor) => [
  {
    offset: 0,
    repeat: 150,
    // @ts-ignore
    symbol: L.Symbol.arrowHead({
      pixelSize: 10,
      polygon: false,
      pathOptions: { stroke: true, color, weight: 2 },
    }),
  },
];

function PolylineDecorator({
  patterns,
  polyline,
  map,
  color = defaultPathColor,
  onClick,
  lineWeight = 2,
}) {
  const [weight, setWeight] = useState(lineWeight);
  useEffect(() => {
    if (!map || !polyline || polyline.length <= 1) return;

    const line = L.polyline(polyline, { color, weight })
      .addEventListener('click', () => onClick())
      .addEventListener('mouseover', () => setWeight(5))
      .addEventListener('mouseout', () => setWeight(2))
      .addTo(map);

    // @ts-ignore
    const decorator = L.polylineDecorator(polyline, {
      patterns,
    }).addTo(map);

    return () => {
      map.removeLayer(line);
      map.removeLayer(decorator);
    };
  }, [map, patterns, polyline, color, onClick, weight, setWeight]);

  return null;
}

function MyComponent({ onPress }) {
  useMapEvents({
    click: (e) => {
      testPath.push([e.latlng.lat, e.latlng.lng]);
      console.log(testPath);
      onPress({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function UserPath({ locations }) {
  return (
    <View testID={'userPathMarker'}>
      <Polyline
        positions={locations}
        pathOptions={{
          dashArray: '5 10',
          color: '#b6b6b6',
          weight: 2,
        }}
      />
    </View>
  );
}

export function Map({
  markers,
  location = [],
  routes,
  setMarker,
  path,
  onPress,
  setWithinBounds,
  isSatelliteView,
  setRouteDetails,
  areas,
  setArea,
  reports,
}: MapProps) {
  const [currentLocation, setCurrentLocation] = useState(location[0]);
  const [map, setMap] = useState(null);
  const [url, setUrl] = useState(
    'https://api.maptiler.com/maps/outdoor-v2/{z}/{x}/{y}.png',
  );
  const [pathColor, setPathColor] = useState(defaultPathColor);

  useEffect(() => {
    setCurrentLocation(location[0]);
  }, [location]);

  useEffect(() => {
    if (currentLocation) {
      const userLocation: [number, number] = [
        currentLocation.lat,
        currentLocation.lng,
      ];
      if (bounds.contains(userLocation)) {
        map.setView(userLocation, 16);
        setWithinBounds(true);
      } else {
        setWithinBounds(false);
      }
    }
  }, [currentLocation, map, routes, setWithinBounds]);

  useEffect(() => {
    setUrl(
      isSatelliteView
        ? 'https://api.maptiler.com/maps/satellite/{z}/{x}/{y}.jpg'
        : 'https://api.maptiler.com/maps/outdoor-v2/{z}/{x}/{y}.png',
    );
    setPathColor(isSatelliteView ? '#fff' : defaultPathColor);
  }, [isSatelliteView]);

  return (
    <MapContainer
      style={styles.container}
      center={parkCenter}
      zoom={16}
      minZoom={16}
      maxZoom={20}
      maxBounds={bounds}
      ref={setMap}
    >
      <MyComponent onPress={onPress} />
      <TileLayer
        bounds={bounds}
        minZoom={16}
        maxZoom={20}
        attribution={
          '\u003ca href="https://www.maptiler.com/copyright/" target="_blank"\u003e\u0026copy; MapTiler\u003c/a\u003e \u003ca href="https://www.openstreetmap.org/copyright" target="_blank"\u003e\u0026copy; OpenStreetMap contributors\u003c/a\u003e'
        }
        url={`${url}?key=${process.env.MAP_KEY}`}
      />
      {areas &&
        areas.map((area) => {
          if (area.coordinates) {
            return (
              <Polygon
                key={area.id}
                positions={area.coordinates}
                // @ts-ignore
                fill={`url(${area.icon})`}
                fillColor={area.icon ? 'transparent' : 'rgba(246, 166, 21, .5)'}
                fillOpacity={1}
                stroke={false}
                eventHandlers={{
                  click: () => {
                    setArea(area);
                  },
                }}
              />
            );
          } else {
            return <></>;
          }
        })}
      {currentLocation && (
        <View testID={'userMarker'} style={styles.userMarker}>
          <CircleMarker
            center={[currentLocation.lat, currentLocation.lng]}
            pathOptions={{ color: 'red' }}
            radius={styles.userMarker.width}
          />
        </View>
      )}
      {location.length > 0 && <UserPath locations={location} />}
      {routes &&
        routes.map((route, index) => (
          <View
            testID={'routeMarker'}
            key={`routeMarker${route.color}${index}`}
          >
            <PolylineDecorator
              key={route.routes && route.routes[0] ? route.routes[0][0] : null}
              color={path ? `rgba(${hexToRgb(route.color)}, .3)` : route.color}
              patterns={arrow(
                path ? `rgba(${hexToRgb(route.color)}, .3)` : route.color,
              )}
              polyline={route.routes}
              map={map}
              onClick={() => setRouteDetails(route)}
            />
          </View>
        ))}
      {path && (
        <View testID={'pathMarker'}>
          {path.routes.map((p) => (
            <PolylineDecorator
              key={p.routes[0][0]}
              color={p.color || pathColor}
              patterns={arrow(p.color || pathColor)}
              polyline={p.routes}
              map={map}
              onClick={() => {}}
              lineWeight={4}
            />
          ))}
        </View>
      )}
      {markers &&
        markers.map((mark) => {
          if (mark.latlng) {
            return (
              <Marker
                position={mark.latlng}
                icon={icon({
                  iconSize: [50, 50],
                  iconAnchor: [25, 50],
                  iconUrl: mark.markerIcon
                    ? mark.markerIcon
                    : isSatelliteView
                    ? pinIconLight
                    : pinIcon,
                })}
                key={mark.title}
                eventHandlers={{
                  click: () => {
                    setMarker(mark);
                    map.setView(mark.latlng, 16);
                  },
                }}
              />
            );
          } else {
            return <></>;
          }
        })}
      {reports &&
        reports.map((report, index) => {
          if (report.latlng) {
            return (
              <LayerGroup>
                <CircleMarker
                  center={report.latlng}
                  radius={30}
                  pathOptions={{
                    color: 'red',
                    fillColor: 'red',
                    fillOpacity: 0.6,
                  }}
                />
                <SVGOverlay
                  bounds={[
                    [report.latlng[0] - 0.0006, report.latlng[1] - 0.0006],
                    [report.latlng[0] + 0.0006, report.latlng[1] + 0.0006],
                  ]}
                >
                  <text
                    x="18"
                    y="48"
                    fontSize={30}
                    fill="white"
                    fontWeight={800}
                  >
                    {index + 1}
                  </text>
                </SVGOverlay>
              </LayerGroup>
            );
          }
        })}
    </MapContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    height: '100%',
    flex: 1,
  },
  userMarker: {
    width: 10,
    height: 10,
  },
});
